// BB.2 — Editor estruturado declarativo (fallback quando não há missão guiada usável).
//
// Cada seção do Business Plan Schema tem uma lista de campos que persistem
// no bloco jsonb correspondente de `business_workspace`. As chaves usadas
// aqui são as MESMAS que o adaptador (`adapter.ts`) já classifica como
// requiredFields / optionalFields — isso garante que salvar via editor
// estruturado atualiza o status determinístico exposto no plano.
//
// Regras de conteúdo (nunca hardcoded em JSX):
// - `type` controla a UI (text | long | money | list | percent)
// - `required` marca o campo como necessário para "Concluir seção"
// - `minLength` (apenas para long/text) reforça o critério do adapter
// - `help` é a copy explicativa em pt-BR
//
// Rascunho vs Concluir: qualquer campo pode ser salvo como rascunho.
// "Concluir seção" só é liberado quando required + minLength forem atendidos
// e o detector de placeholders do adapter não sinalizar suspeita.

import type { BlockKey } from "@/lib/business-blocks";

export type StructuredFieldType = "text" | "long" | "money" | "list" | "percent" | "logo";

export type StructuredField = {
  key: string;
  label: string;
  type: StructuredFieldType;
  help?: string;
  placeholder?: string;
  required?: boolean;
  minLength?: number;
};

export type StructuredSectionConfig = {
  sectionKey: BlockKey;
  block: BlockKey; // sempre === sectionKey nas 9 seções BB.2
  intro: string;
  fields: StructuredField[];
};

const CONFIGS: StructuredSectionConfig[] = [
  {
    sectionKey: "model",
    block: "model",
    intro: "Defina como o negócio ganha dinheiro, o que entrega e qual é o principal custo. Essa é a base para todas as outras decisões.",
    fields: [
      { key: "revenue_model", label: "Como o negócio ganha dinheiro", type: "text", required: true, help: "Ex.: venda por serviço prestado, mensalidade, comissão por evento.", placeholder: "Ex.: cobrança por evento fechado" },
      { key: "delivery_mode", label: "Forma de entrega", type: "text", required: true, help: "Como o cliente recebe o valor: presencial, online, híbrido, atendimento no local.", placeholder: "Ex.: atendimento presencial no local do evento" },
      { key: "main_cost", label: "Principal custo do negócio", type: "text", required: true, help: "O maior gasto recorrente para operar. Ajuda a entender margem.", placeholder: "Ex.: equipe freelancer por evento" },
      { key: "ticket_average", label: "Ticket médio estimado", type: "money", help: "Valor médio cobrado por venda. Estimativa é suficiente agora." },
      { key: "promise", label: "Promessa central", type: "long", minLength: 12, help: "Em uma frase: qual é o resultado que o cliente compra?", placeholder: "Ex.: microcasamentos inesquecíveis, com curadoria pessoal e sem stress." },
    ],
  },
  {
    sectionKey: "niche",
    block: "niche",
    intro: "O nicho é o recorte que direciona público, oferta e canais. Quanto mais específico, mais fácil vender.",
    fields: [
      { key: "niche_name", label: "Nicho escolhido", type: "text", required: true, help: "Um nome curto que define exatamente para quem e para qual necessidade seu negócio foi criado.", placeholder: "Descreva o recorte específico do seu negócio" },
      { key: "segment", label: "Segmento de mercado", type: "text", help: "O setor mais amplo ao qual o seu nicho pertence.", placeholder: "Informe o mercado em que seu negócio está inserido" },
      { key: "why", label: "Por que este nicho", type: "long", minLength: 16, help: "Conte o que motivou essa escolha e qual oportunidade você acredita existir nesse mercado.", placeholder: "Explique por que você escolheu esse nicho" },
    ],
  },
  {
    sectionKey: "audience",
    block: "audience",
    intro: "Descreva a pessoa que compra. Quanto mais concreto, melhor a comunicação e a oferta.",
    fields: [
      { key: "persona_name", label: "Persona (cliente-alvo)", type: "text", required: true, help: "Um nome/rotulo curto que representa o público principal.", placeholder: "Ex.: noivas de segundo casamento, 30–45 anos" },
      { key: "pain", label: "Dor principal do cliente", type: "long", required: true, minLength: 20, help: "O problema concreto que o cliente vive antes de contratar." },
      { key: "context", label: "Contexto de compra", type: "long", help: "Onde e quando o cliente decide contratar. O que precede a decisão." },
      { key: "age_range", label: "Faixa etária", type: "text" },
      { key: "region", label: "Onde o cliente está", type: "text", help: "Cidade, região ou alcance geográfico." },
    ],
  },
  {
    sectionKey: "offer",
    block: "offer",
    intro: "A proposta de valor é o que faz o cliente escolher você em vez de outra opção. Precisa ser clara e específica.",
    fields: [
      { key: "headline", label: "Frase principal", type: "text", required: true, help: "Uma linha que sintetiza o que o negócio faz e para quem.", placeholder: "Ex.: microcasamentos com curadoria e serviço completo em um único contato." },
      { key: "promise", label: "Promessa central", type: "long", required: true, minLength: 24, help: "O resultado transformador. Escreva do ponto de vista do cliente." },
      { key: "differentiators", label: "Diferenciais", type: "list", help: "Separe por vírgula. Ex.: atendimento personalizado, fornecedores próprios, preço fechado." },
      { key: "guarantee", label: "Garantia oferecida", type: "text", help: "Qual segurança o cliente tem se algo der errado." },
    ],
  },
  {
    sectionKey: "products",
    block: "products",
    intro: "Detalhe o que exatamente é vendido. Sem clareza aqui, preço e operação ficam frouxos.",
    fields: [
      { key: "core_product", label: "Produto ou serviço principal", type: "text", required: true, help: "O item que representa a maior parte da receita.", placeholder: "Ex.: pacote cerimonial completo (curadoria + coordenação no dia)" },
      { key: "catalog", label: "Ofertas complementares", type: "list", help: "Outras opções vendidas. Separe por vírgula." },
      { key: "upsell", label: "Upsell ou adicional", type: "text", help: "O que pode ser oferecido para aumentar ticket." },
      { key: "format", label: "Formato da entrega", type: "text", help: "Como o produto/serviço é entregue na prática." },
    ],
  },
  {
    sectionKey: "pricing",
    block: "pricing",
    intro: "Sem margem definida, o negócio vende e ainda assim pode quebrar. Coloque números realistas.",
    fields: [
      { key: "price", label: "Preço praticado", type: "money", required: true, help: "Preço médio cobrado pelo produto/serviço principal." },
      { key: "cost", label: "Custo direto", type: "money", required: true, help: "Quanto sai do caixa para entregar cada venda (fornecedores, matéria-prima, comissão)." },
      { key: "margin_pct", label: "Margem estimada", type: "percent", help: "Percentual que sobra depois do custo direto." },
      { key: "payment_terms", label: "Condições de pagamento", type: "text", help: "Ex.: sinal 30% + saldo 15 dias antes do evento." },
      { key: "recurrence", label: "Recorrência", type: "text", help: "Frequência típica: pontual, mensal, sazonal." },
    ],
  },
  {
    sectionKey: "brand",
    block: "brand",
    intro: "Marca é a memória do cliente. Precisa combinar com o público e a promessa de valor.",
    fields: [
      { key: "name", label: "Nome da marca", type: "text", required: true, help: "Nome fantasia usado no mercado." },
      { key: "tagline", label: "Tagline", type: "text", help: "Uma linha curta que reforça o posicionamento." },
      { key: "tone", label: "Tom de voz", type: "text", help: "Como a marca fala. Ex.: acolhedora, técnica, próxima, elegante." },
      { key: "colors", label: "Paleta / referências", type: "list", help: "Cores, mood, referências. Separe por vírgula." },
      { key: "logo_url", label: "Logomarca", type: "logo", help: "Gere uma logomarca com IA a partir do nome, tom e paleta acima — ou deixe em branco para definir depois." },
    ],
  },
  {
    sectionKey: "channels",
    block: "channels",
    intro: "Escolher o canal errado é o motivo #1 de negócios travados sem venda. Onde o cliente busca por você?",
    fields: [
      { key: "primary", label: "Canal principal", type: "text", required: true, help: "Onde vem a maior parte dos clientes (ou virá).", placeholder: "Ex.: Instagram + indicação de fornecedores parceiros" },
      { key: "secondary", label: "Canais de apoio", type: "list", help: "Outros canais que reforçam. Separe por vírgula." },
      { key: "budget", label: "Investimento mensal", type: "money", help: "Quanto você pode investir por mês em aquisição." },
    ],
  },
  {
    sectionKey: "sales_plan",
    block: "sales_plan",
    intro: "Transforma o plano em ação. Sem plano de vendas, o negócio não sai do papel.",
    fields: [
      { key: "first_clients", label: "Primeiros clientes-alvo", type: "long", required: true, minLength: 24, help: "Quem você pode contatar já esta semana. Nomes, listas, grupos concretos." },
      { key: "goal_30d", label: "Meta em 30 dias", type: "text", required: true, help: "Uma meta específica: nº de contatos, propostas, vendas fechadas." },
      { key: "weekly_actions", label: "Ações semanais", type: "list", help: "O que você repete toda semana para gerar oportunidade. Separe por vírgula." },
    ],
  },
];

const CONFIG_BY_KEY: Record<string, StructuredSectionConfig> = Object.fromEntries(
  CONFIGS.map((c) => [c.sectionKey, c]),
);

export function getStructuredConfig(sectionKey: BlockKey): StructuredSectionConfig | null {
  return CONFIG_BY_KEY[sectionKey] ?? null;
}

export const ALL_STRUCTURED_CONFIGS = CONFIGS;

/**
 * Serializa o valor de um campo estruturado antes de gravar no jsonb.
 * - list → string[] (split por vírgula, itens trimados, vazios removidos)
 * - money/percent → string (mantém formato bruto — o adapter/formatter
 *   já sabe interpretar strings numéricas)
 * - text/long → string trimada
 */
export function serializeFieldValue(field: StructuredField, raw: unknown): unknown {
  if (raw == null) return null;
  if (field.type === "list") {
    const s = String(raw).trim();
    if (!s) return [];
    return s.split(",").map((x) => x.trim()).filter(Boolean);
  }
  return String(raw).trim();
}

/**
 * Volta um valor do jsonb para o formato de input do editor estruturado.
 * Espelha `serializeFieldValue` (list ↔ string separada por vírgula).
 */
export function deserializeFieldValue(field: StructuredField, stored: unknown): string {
  if (stored == null) return "";
  if (field.type === "list") {
    if (Array.isArray(stored)) return stored.map((x) => (typeof x === "string" ? x : String((x as any)?.label ?? (x as any)?.value ?? ""))).filter(Boolean).join(", ");
    return String(stored);
  }
  if (typeof stored === "object") {
    const o = stored as any;
    return String(o.label ?? o.value ?? "");
  }
  return String(stored);
}
